import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, MessageCircle, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import GallerySection from "../components/GallerySection";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Common";
import { IMAGES, BAKHOOR_BLENDS, BAKHOOR_GALLERY, waLink } from "../mock/mock";

const bakhoorJsonLd = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Bakhoor & Signature Oud Blends",
  image: IMAGES.bakhoorBowl,
  description: "100% organic, certified bakhoor incense blends built as fragrance pyramids across top, heart and base notes. 13+ signature blends including Tobacco Oud, Taif Rose and Saffron Rose. Bespoke blends on request.",
  brand: { "@type": "Brand", name: "Meilleur Oudh & Aromas" },
  category: "Bakhoor / Incense",
  offers: {
    "@type": "AggregateOffer",
    availability: "https://schema.org/InStock",
    priceCurrency: "USD",
    seller: { "@type": "Organization", name: "Meilleur Oudh & Aromas" },
  },
};

const STATS = [
  { v: "100%", l: "Organic, Certified" },
  { v: "13+", l: "Signature Blends" },
  { v: "Global", l: "Worldwide Shipping" },
  { v: "Bespoke", l: "Custom on Request" },
];

const Bakhoor = () => {
  const [active, setActive] = useState(0);

  return (
    <div className="bg-noir">
      <Seo
        title="Bakhoor — 100% Organic Signature Oud Incense Blends | Meilleur"
        description="Hand-blended, 100% organic bakhoor incense in 13+ signature oud blends — Tobacco Oud, Taif Rose, Saffron Rose and more. Bespoke fragrance pyramids made to order. Worldwide shipping."
        path="/bakhoor"
        keywords="bakhoor, oud bakhoor, agarwood incense, bakhoor blends, buy bakhoor, tobacco oud, taif rose, saffron rose, bespoke bakhoor, oud incense supplier"
        jsonLd={bakhoorJsonLd}
      />
      <PageHero eyebrow="Meilleur Oudh & Aromas" title="Bakhoor &" accent="Blends" image={IMAGES.bakhoorBowl} crumbs={["Collections", "Bakhoor"]} />

      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal><SectionHeading eyebrow="Value-Added Manufacturing" title="A fragrance pyramid," accent="hand-built" /></Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-[var(--ivory-muted)] text-lg leading-relaxed font-body font-light max-w-2xl">
                100% organic fragrances, produced with certification and available with worldwide shipping. Every blend
                is composed as a fragrance pyramid across top, heart and base notes — and we customise blends to a
                partner's own specification on request.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
                {STATS.map((s) => (
                  <div key={s.l} className="border-l border-[var(--line)] pl-4">
                    <div className="font-serif-display text-2xl md:text-3xl gold-text">{s.v}</div>
                    <div className="text-[0.66rem] tracking-[0.14em] uppercase text-[var(--ivory-muted)] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={140}>
              <div className="img-zoom relative">
                <img src={IMAGES.incenseStick} alt="Bakhoor smoke" className="w-full h-[480px] object-cover" />
                <div className="absolute inset-0 border border-[var(--line)]" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Blends explorer */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="The Range" title="Signature" accent="blends" align="center" /></Reveal>
          <div className="mt-16 grid lg:grid-cols-12 gap-10">
            {/* list */}
            <div className="lg:col-span-5 max-h-[520px] overflow-y-auto pr-2 border border-[var(--line)]">
              {BAKHOOR_BLENDS.map((b, i) => (
                <button
                  key={b.n}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`w-full text-left flex items-center gap-5 px-6 py-4 border-b border-[var(--line)] transition-colors ${
                    active === i ? "bg-[rgba(20,81,60,0.08)]" : "hover:bg-[rgba(20,81,60,0.04)]"
                  }`}
                >
                  <span className={`font-serif-display text-2xl ${active === i ? "gold-text" : "text-[var(--ivory-muted)] opacity-50"}`}>{b.n}</span>
                  <span className={`font-serif-display text-xl ${active === i ? "text-[var(--gold-light)]" : "text-[var(--ivory)]"}`}>{b.name}</span>
                </button>
              ))}
            </div>
            {/* detail */}
            <div className="lg:col-span-7">
              <div className="lux-card p-10 md:p-14 h-full flex flex-col justify-center">
                <Flame size={28} className="text-[var(--gold)] mb-8" />
                <div className="eyebrow mb-4">Blend {BAKHOOR_BLENDS[active].n}</div>
                <h3 className="font-serif-display text-4xl md:text-5xl mb-8 gold-text">{BAKHOOR_BLENDS[active].name}</h3>
                <div className="hairline opacity-40 mb-8" />
                <div className="eyebrow text-[0.6rem] mb-4">Fragrance Notes</div>
                <div className="flex flex-wrap gap-2.5">
                  {BAKHOOR_BLENDS[active].notes.split(",").map((n) => (
                    <span key={n} className="border border-[var(--line)] px-4 py-2 text-sm text-[var(--ivory-muted)] font-body">{n.trim()}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GallerySection
        eyebrow="The Gallery"
        title="Smoke &"
        accent="ritual"
        images={BAKHOOR_GALLERY}
        label="Bakhoor"
      />

      <section className="py-28 text-center">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow mb-6">Bespoke Fragrance</p>
            <h2 className="font-serif-display text-4xl md:text-6xl mb-6 max-w-3xl mx-auto">Craft your own <span className="gold-text italic">signature blend</span></h2>
            <p className="text-[var(--ivory-muted)] max-w-xl mx-auto mb-10 font-body font-light">
              We develop bespoke bakhoor to your specification — top, heart and base, built around our own oud.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/blend-finder" className="btn-gold">
                <Sparkles size={15} /> Find Your Blend
              </Link>
              <a href={waLink("Bakhoor blends")} target="_blank" rel="noopener noreferrer" className="btn-ghost">
                <MessageCircle size={15} /> WhatsApp Enquiry
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Bakhoor;
